import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

// A corrupted or hand-edited value must not crash the app on load
const safeParse = (raw: string | null, defaultValue: any) => {
  if (raw === null) return defaultValue;
  try {
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
};

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState(() => {
    try {
      return safeParse(localStorage.getItem(key), defaultValue);
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? safeParse(e.newValue, defaultValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: ValueType) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;

      try {
        localStorage.setItem(key, JSON.stringify(result));
      } catch {
        // Storage full or blocked: keep the value in memory only
      }

      return result;
    });
  };

  return [value, setValueInLocalStorage];
}
